import CloseSmall from '@crehana/compass.icons.outline/CloseSmall';
import Trash2 from '@crehana/compass.icons.outline/Trash2';
import { BaseDialog, useUITheme } from '@crehana/compass.ui';
import { alert, primaryLight, warning } from '@crehana/tailwindcss/colors';
import { clsx } from 'clsx';

import {
  WrapperIcon,
  WrapperModalButtons,
  WrapperModalContentHeader,
} from './ModernModal.styles';
import { type ModernModalProps, SIZE_ICON_MODERN_MODAL } from './types';

const ModernModal: React.FC<ModernModalProps> = ({
  title,
  isOpen,
  onClose,
  children,
  onConfirm,
  withHeader,
  centeredButtons,
  labelCloseButton,
  withTopCloseButton,
  labelConfirmButton,
  disableConfirmButton,
  typeConfirmButton = 'primary',
  headerIcon,
  className,
}) => {
  const { isDark } = useUITheme();
  const modalClasses = `${className} dark:tw-bg-elevation-dark-00 tw-relative`;
  const confirmationButtonClassNames = clsx('ml-8', {
    'tw-btn-primary-xs': typeConfirmButton === 'primary',
    'tw-btn-delete-xs': typeConfirmButton === 'alert',
    'tw-btn-disabled': disableConfirmButton,
  });
  const cancelButtonClassNames = clsx('tw-btn-tertiary-xs', {
    'tw-text-neutral-dark-500': isDark,
  });

  const colorIconHeader = {
    primary: primaryLight,
    alert,
    warning,
  };

  const iconProps = {
    size: 20,
    className: 'tw-text-alert-500',
  };

  return (
    <BaseDialog open={isOpen} onDismiss={onClose} className={modalClasses}>
      <div className={clsx(className || 'tw-w-[562px]')}>
        {withHeader && (
          <WrapperModalContentHeader>
            <WrapperIcon
              type={SIZE_ICON_MODERN_MODAL.S}
              customColor={
                colorIconHeader[
                  typeConfirmButton as 'primary' | 'alert' | 'warning'
                ]
              }
            >
              {headerIcon ? headerIcon : <Trash2 {...iconProps} />}
            </WrapperIcon>
            <p className="ml-12 tw-font-subtitle4 dark:tw-text-neutral-light-100">
              {title}
            </p>
          </WrapperModalContentHeader>
        )}

        {withTopCloseButton && (
          <button
            type="button"
            onClick={onClose}
            className="tw-absolute tw-top-1.2 tw-right-1.2"
          >
            <CloseSmall size={24} className="tw-text-neutral-light-700" />
          </button>
        )}

        <div className="tw-flex tw-flex-col tw-mb-1.2 px-24">{children}</div>
        <WrapperModalButtons isDark={isDark} center={centeredButtons}>
          <div className="tw-flex tw-items-center">
            {labelCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className={cancelButtonClassNames}
              >
                {labelCloseButton}
              </button>
            )}
            {labelConfirmButton && (
              <button
                type="button"
                className={confirmationButtonClassNames}
                disabled={disableConfirmButton}
                onClick={onConfirm}
              >
                {labelConfirmButton}
              </button>
            )}
          </div>
        </WrapperModalButtons>
      </div>
    </BaseDialog>
  );
};

export default ModernModal;
